import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from './api';
import { useOrgId } from './org';

/** Global search in the top bar (document 05): GET /orgs/:orgId/search?q=&limit=. */
export type SearchGroupKey = 'recent' | 'transactions' | 'contacts' | 'accounts' | 'items';

export interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  /** ISO yyyy-MM-dd. */
  date?: string;
  /** Decimal string in `currency`. */
  amount?: string;
  currency?: string;
  inactive?: true;
  /** Web route, relative. */
  href: string;
}

export interface SearchView {
  q: string;
  groups: Array<{ key: SearchGroupKey; results: SearchResult[] }>;
}

export const GROUP_LABELS: Record<SearchGroupKey, string> = {
  recent: 'Recent transactions',
  transactions: 'Transactions',
  contacts: 'Customers and vendors',
  accounts: 'Accounts',
  items: 'Products and services',
};

const GROUP_ORDER: SearchGroupKey[] = ['recent', 'transactions', 'contacts', 'accounts', 'items'];

export interface SearchSection {
  key: SearchGroupKey;
  label: string;
  /** Each result with its position in the flat keyboard order. */
  rows: Array<{ index: number; result: SearchResult }>;
}

/** Non-empty groups in display order, with a running index for keyboard navigation. */
export function toSections(view: SearchView | undefined): {
  sections: SearchSection[];
  flat: SearchResult[];
} {
  const flat: SearchResult[] = [];
  const sections: SearchSection[] = [];
  const groups = [...(view?.groups ?? [])].sort(
    (a, b) => GROUP_ORDER.indexOf(a.key) - GROUP_ORDER.indexOf(b.key),
  );
  for (const g of groups) {
    if (!g.results.length) continue;
    sections.push({
      key: g.key,
      label: GROUP_LABELS[g.key] ?? g.key,
      rows: g.results.map((result) => ({ index: flat.push(result) - 1, result })),
    });
  }
  return { sections, flat };
}

/** ArrowDown/ArrowUp over `count` rows, wrapping; -1 means nothing is highlighted. */
export function moveActive(current: number, delta: 1 | -1, count: number): number {
  if (count <= 0) return -1;
  if (current < 0) return delta === 1 ? 0 : count - 1;
  return (current + delta + count) % count;
}

/** True when a key press belongs to a field the user is typing in (so `/` must not steal it). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== 'string') return false;
  const el = target as HTMLElement;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

/** `value`, once it has stopped changing for `ms`. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function useGlobalSearch(q: string, enabled: boolean) {
  const orgId = useOrgId();
  const term = q.trim();
  return useQuery({
    queryKey: ['search', orgId, term],
    queryFn: () =>
      api<SearchView>(`/orgs/${orgId}/search?${new URLSearchParams({ q: term, limit: '5' })}`),
    enabled: enabled && Boolean(orgId),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}
